import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { dayjs, duration } from 'date';
import { logger } from 'monitor';

import {
  useApiEvidenceUploadCreate,
  useApiEvidenceUploadUpdate,
  useApiEvidenceV1DataPartialUpdate,
  useApiInspectionsV1EvidenceUploadCreate,
  useApiInspectionsV1EvidenceUploadUpdate,
  useApiLogisticsV1LoadsEvidenceUploadCreate,
  useApiLogisticsV1LoadsEvidenceUploadUpdate,
  useApiQualityExternalUploadV1EvidenceUploadCreate,
  useApiQualityExternalUploadV1EvidenceUploadUpdate,
  useApiQualitySessionsV1SiteMaterialSessionsEvidenceUploadCreate,
  useApiQualitySessionsV1SiteMaterialSessionsEvidenceUploadUpdate,
  useApiQualitySessionsV1TradeCompanySessionsEvidenceUploadCreate,
  useApiQualitySessionsV1TradeCompanySessionsEvidenceUploadUpdate,
  useApiSamplesV1SamplesUploadCreate,
  useApiSamplesV1SamplesUploadUpdate,
  useApiSamplesV1SiteMaterialSessionSampleEvidenceUploadCreate,
  useApiSamplesV1SiteMaterialSessionSampleEvidenceUploadUpdate,
  useApiSamplesV1TradeCompanySessionSampleEvidenceUploadCreate,
  useApiSamplesV1TradeCompanySessionSampleEvidenceUploadUpdate,
  useApiTradeV1SiteMaterialsEvidenceUploadCreate,
  useApiTradeV1SiteMaterialsEvidenceUploadUpdate,
} from '../../client/api/api';
import { EvidenceTypeEnum, type ProcessUploadResponse } from '../../schemas';
import { useUploadFile } from '../useUploadFile/useUploadFile';

dayjs.extend(duration);
export const prepareUploadMetrics = (
  photo: File,
  startTime: number,
  uploadStartTime: number,
  fileUploadedTime: number,
  fileProcessingDone: number
) => {
  const fullProcessTime = dayjs.duration(fileProcessingDone - startTime, 'milliseconds').asSeconds();
  const uploadTime = dayjs.duration(fileUploadedTime - startTime, 'milliseconds').asSeconds();
  const uploadFileTime = dayjs.duration(fileUploadedTime - uploadStartTime, 'milliseconds').asSeconds();
  const photoSizeInBits = photo.size * 8;
  return {
    full_process_time: fullProcessTime,
    // bytes
    size: photo.size,
    upload_file_time: uploadTime,
    // bps
    upload_speed: photoSizeInBits / uploadFileTime,
  };
};

export type UploadEvidenceData = {
  photo: File;
  onProgress?: (progress: number) => void;
  parentId: string;
  model?: string;
  startTime: number;
  evidenceType?: EvidenceTypeEnum;
  metadata?: Record<string, unknown>;
};

export type EvidenceUploadMode =
  | 'demo'
  | 'inspector'
  | 'site-material'
  | 'sample'
  | 'load'
  | 'trade-company-session'
  | 'trade-company-session-sample'
  | 'external-evidence'
  | 'site-material-session'
  | 'site-material-session-sample';

export const useUploadEvidence = (
  mode: EvidenceUploadMode,
  mutationOptions?: UseMutationOptions<ProcessUploadResponse, unknown, UploadEvidenceData, unknown>
) => {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: uploadData } = useApiEvidenceV1DataPartialUpdate();

  const { mutateAsync: generateDemoUploadUrl } = useApiEvidenceUploadCreate();
  const { mutateAsync: processDemoFile } = useApiEvidenceUploadUpdate();

  const { mutateAsync: generateSampleUploadUrl } = useApiSamplesV1SamplesUploadCreate();
  const { mutateAsync: processSampleFile } = useApiSamplesV1SamplesUploadUpdate();

  const { mutateAsync: generateInspectionUploadUrl } = useApiInspectionsV1EvidenceUploadCreate();
  const { mutateAsync: processInspectionFile } = useApiInspectionsV1EvidenceUploadUpdate();

  const { mutateAsync: generateSiteMaterialUploadUrl } = useApiTradeV1SiteMaterialsEvidenceUploadCreate();
  const { mutateAsync: processSiteMaterialFile } = useApiTradeV1SiteMaterialsEvidenceUploadUpdate();

  const { mutateAsync: generateLoadUploadUrl } = useApiLogisticsV1LoadsEvidenceUploadCreate();
  const { mutateAsync: processLoadFile } = useApiLogisticsV1LoadsEvidenceUploadUpdate();

  const { mutateAsync: generateTradeCompanySessionEvidenceUploadUrl } =
    useApiQualitySessionsV1TradeCompanySessionsEvidenceUploadCreate();
  const { mutateAsync: processTradeCompanySessionEvidenceFile } =
    useApiQualitySessionsV1TradeCompanySessionsEvidenceUploadUpdate();

  const { mutateAsync: generateTradeCompanySampleSessionEvidenceUploadUrl } =
    useApiSamplesV1TradeCompanySessionSampleEvidenceUploadCreate();
  const { mutateAsync: processTradeCompanySampleSessionEvidenceFile } =
    useApiSamplesV1TradeCompanySessionSampleEvidenceUploadUpdate();

  const { mutateAsync: generateExternalEvidenceUploadUrl } = useApiQualityExternalUploadV1EvidenceUploadCreate();
  const { mutateAsync: processExternalEvidenceFile } = useApiQualityExternalUploadV1EvidenceUploadUpdate();

  const { mutateAsync: generateSiteMaterialSessionEvidenceUploadUrl } =
    useApiQualitySessionsV1SiteMaterialSessionsEvidenceUploadCreate();
  const { mutateAsync: processSiteMaterialSessionEvidenceFile } =
    useApiQualitySessionsV1SiteMaterialSessionsEvidenceUploadUpdate();

  const { mutateAsync: generateSiteMaterialSessionSampleEvidenceUploadUrl } =
    useApiSamplesV1SiteMaterialSessionSampleEvidenceUploadCreate();
  const { mutateAsync: processSiteMaterialSessionSampleEvidenceFile } =
    useApiSamplesV1SiteMaterialSessionSampleEvidenceUploadUpdate();

  const generateUploadUrl = (photo: File, parentId: string) => {
    logger.debug('generateUploadUrl', { mode, parentId, photo });
    if (mode === 'demo') {
      return generateDemoUploadUrl({ data: { content_type: photo.type } });
    }

    if (mode === 'sample') {
      return generateSampleUploadUrl({ data: { content_type: photo.type }, id: Number(parentId) });
    }

    if (mode === 'inspector') {
      return generateInspectionUploadUrl({
        data: { content_type: photo.type },
        slug: parentId,
      });
    }

    if (mode === 'load') {
      return generateLoadUploadUrl({
        data: { content_type: photo.type },
        loadId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session') {
      return generateTradeCompanySessionEvidenceUploadUrl({
        data: { content_type: photo.type },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session-sample') {
      return generateTradeCompanySampleSessionEvidenceUploadUrl({
        data: { content_type: photo.type },
        id: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return generateExternalEvidenceUploadUrl({
        data: { content_type: photo.type },
        uuid: parentId,
      });
    }

    if (mode === 'site-material-session') {
      return generateSiteMaterialSessionEvidenceUploadUrl({
        data: { content_type: photo.type },
        siteMaterialSessionId: Number(parentId),
      });
    }

    if (mode === 'site-material-session-sample') {
      return generateSiteMaterialSessionSampleEvidenceUploadUrl({
        data: { content_type: photo.type },
        id: Number(parentId),
      });
    }

    return generateSiteMaterialUploadUrl({
      data: { content_type: photo.type },
      siteMaterialId: parentId,
    });
  };

  const registerEvidence = (
    file_id: string,
    parentId: string,
    model?: string,
    evidenceType: EvidenceTypeEnum = EvidenceTypeEnum.BP,
    metadata?: Record<string, unknown>
  ) => {
    logger.debug('registerEvidence', { evidenceType, file_id, mode, model, parentId });
    if (mode === 'demo') {
      return processDemoFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'demo',
          },
          model,
        },
      });
    }
    if (mode === 'sample') {
      return processSampleFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            inspection_slug: metadata?.inspection_slug,
            origin: metadata?.origin || 'sample',
          },
          model,
        },
        id: Number(parentId),
      });
    }
    if (mode === 'inspector') {
      return processInspectionFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            inspection_slug: parentId,
            origin: 'inspection',
          },
        },
        slug: parentId,
      });
    }

    if (mode === 'site-material') {
      return processSiteMaterialFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'site-material',
          },
          model,
        },
        siteMaterialId: parentId,
      });
    }

    if (mode === 'load') {
      return processLoadFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'load',
          },
          model,
        },
        loadId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session') {
      return processTradeCompanySessionEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'trade-company-session',
          },
          model,
        },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session-sample') {
      return processTradeCompanySampleSessionEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'trade-company-session-sample',
          },
          model,
        },
        id: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return processExternalEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'external-evidence',
          },
          model,
        },
        uuid: parentId,
      });
    }

    if (mode === 'site-material-session') {
      return processSiteMaterialSessionEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'site-material-session',
          },
          model,
        },
        siteMaterialSessionId: Number(parentId),
      });
    }
    if (mode === 'site-material-session-sample') {
      return processSiteMaterialSessionSampleEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'site-material-session-sample',
          },
          model,
        },
        id: Number(parentId),
      });
    }

    throw new Error('Invalid evidence mode');
  };

  return useMutation({
    ...mutationOptions,
    mutationFn: async (data: UploadEvidenceData) => {
      const { photo, onProgress, parentId, model, metadata } = data;
      const { upload_url, file_id } = await generateUploadUrl(photo, parentId);

      logger.info('Uploading evidence', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const uploadStartTime = performance.now();

      await uploadFile({
        file: photo,
        headers: { 'content-type': photo.type },
        onProgress,
        url: upload_url,
      });

      logger.info('Evidence uploaded; analysing', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const fileUploadedTime = performance.now();
      const analysis = await registerEvidence(file_id, parentId, model, data.evidenceType, metadata);

      logger.info('Evidence analysis completed', {
        analysis,
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const fileProcessingDone = performance.now();
      const metrics = prepareUploadMetrics(
        photo,
        data.startTime,
        uploadStartTime,
        fileUploadedTime,
        fileProcessingDone
      );
      uploadData({ data: metrics, id: analysis.image.id });

      return analysis;
    },
  });
};
